import { Body, Caption, SectionTitle, VButton, VIconActionButton, VInput, cn } from '@vrzl/ui';
import { Plus, Trash2, X } from 'lucide-react';

import type { OverviewBlock } from '../api/types';
import type { BlockErrors } from '../utils/editBlocks';

import { CriterionPicker, type CriterionOption } from './criterionPicker';
import { WidthMenu } from './widthMenu';

type BlockEditorPanelProps = {
  block: OverviewBlock;
  index: number;
  count: number;
  label: string;
  errors: BlockErrors;
  options: readonly CriterionOption[];
  nameByCode: ReadonlyMap<string, string>;
  usedIn: (code: string) => string[];
  onChange: (block: OverviewBlock) => void;
  onAddBlock: () => void;
  onRemove: () => void;
};

export function BlockEditorPanel({
  block,
  index,
  count,
  label,
  errors,
  options,
  nameByCode,
  usedIn,
  onChange,
  onAddBlock,
  onRemove,
}: BlockEditorPanelProps) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <Caption>
          Geselecteerd blok · {index + 1} van {count}
        </Caption>
        <SectionTitle className="break-words">{label}</SectionTitle>
      </div>

      <VInput
        label="Titel (optioneel)"
        value={block.title ?? ''}
        placeholder="Zonder titel: de namen van de criteria"
        onChange={(event) => onChange({ ...block, title: event.target.value })}
      />

      <WidthMenu value={block.width} onChange={(width) => onChange({ ...block, width })} />

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <Body as="span" weight="medium">
            Criteria in dit blok
          </Body>
          <Caption>In deze volgorde in het blok</Caption>
        </div>
        {block.criteria.length > 0 && (
          <ul className="flex flex-col gap-2">
            {block.criteria.map((code) => {
              const name = nameByCode.get(code);
              return (
                <li
                  key={code}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-3 py-2',
                    name ? 'border-border' : 'border-error-600',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <Body as="span" className="block truncate">
                      {name ?? code}
                    </Body>
                    <Caption className="block break-all font-mono">{code}</Caption>
                  </div>
                  <VIconActionButton
                    icon={X}
                    label={`Verwijder ${name ?? code}`}
                    onClick={() =>
                      onChange({ ...block, criteria: block.criteria.filter((c) => c !== code) })
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
        {errors.criteria && (
          <Body as="span" tone="error">
            {errors.criteria}
          </Body>
        )}
      </div>

      <CriterionPicker
        options={options}
        exclude={block.criteria}
        usedIn={usedIn}
        onAdd={(code) => onChange({ ...block, criteria: [...block.criteria, code] })}
      />

      <div className="flex items-start gap-2 border-t border-border pt-4">
        <VButton type="button" variant="ghost" icon={Trash2} onClick={onRemove}>
          Blok verwijderen
        </VButton>
        <VButton type="button" variant="outline" icon={Plus} onClick={onAddBlock}>
          Blok aanmaken
        </VButton>
      </div>
    </div>
  );
}
